import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "react-tooltip/dist/react-tooltip.css";
import ImageNotFound from "../../images/imagenotfound.jpg";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { formatPhoneNumber } from "../../globalFunctions/PhoneNumberFormatter";
import AddToCartModal from "../../pages/EventCreation/Components/AddtoCartModal";
import AddToCartServiceModal from "../../pages/Store/AddToCartServiceModal";

const getPricingMode = (priceTypeId, priceTypesList, lang) => {
  const found = priceTypesList?.find((x) => x.id == priceTypeId);
  const label =
    (lang === "fr" ? found?.nameFr : found?.nameEn)?.toLowerCase() || "";
  if (label.includes("fixe") || label.includes("fixed")) return "fixed";
  if (label.includes("journée") || label.includes("day")) return "day";
  if (label.includes("personne") || label.includes("person")) return "person";
  return "unit";
};

const ProductCard = ({
  product,
  openEditModal,
  lang: forcedLang,
  priceTypesList,
  showAddToCartButton = false,
  onAddToCart,
  isService = false,
}) => {
  const { t, i18n } = useTranslation();
  const lang = forcedLang ?? i18n.language.split("-")[0];

  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedOptionIndexes, setSelectedOptionIndexes] = useState({});
  const [isSmallImage, setIsSmallImage] = useState(false);

  const [modalServiceDetails, setModalServiceDetails] = useState(null);
  const [modalProductDetails, setModalProductDetails] = useState(null);
  const [modalIsService, setModalIsService] = useState(false);
  const [modalAvailableDates, setModalAvailableDates] = useState(null);
  const [modalPricingMode, setModalPricingMode] = useState("unit");

  const item = useMemo(
    () => product?.service ?? product?.product ?? product ?? {},
    [product]
  );
  const inventoryLimits = useMemo(
    () => item?.productInventoryLimits ?? null,
    [item]
  );

  if (!item) return null;

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setIsSmallImage(naturalWidth < 300 || naturalHeight < 300);
  };

  const unitSuffix = () => {
    const found = priceTypesList?.find((type) => type.id == item?.priceTypeId);
    if (!found || found.id === 0 || found.id === 5) return "";
    return ` / ${lang === "en" ? found.nameEn : found.nameFr}`;
  };

  const priceBlock = () => (
    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 via-black/50 p-4 flex justify-between items-center">
      <p className="text-gray-100 font-bold text-xl mt-4">
        ${item?.price}
        {unitSuffix()}
      </p>
    </div>
  );

  const headerImage = () => (
    <div className="relative h-48 flex items-center justify-center overflow-hidden bg-white">
      <img
        src={
          item?.tempImageUrl ||
          item?.image_url ||
          item?.imageUrl ||
          ImageNotFound
        }
        alt={item?.nameFr || item?.nameEn || ""}
        onLoad={handleImageLoad}
        loading="lazy"
        className={`w-full h-48 transition-all duration-300 ${
          isSmallImage ? "object-contain bg-primary p-2" : "object-cover"
        }`}
      />
      {priceBlock()}
    </div>
  );

  const titleCategory = () => (
    <>
      {(item?.nameFr || item?.nameEn) && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 overflow-hidden line-clamp-1">
            {lang === "fr" ? item?.nameFr : item?.nameEn}
          </h2>
          {item?.category && (
            <p className="text-xs italic text-gray-600">
              {t("categories." + item.category)}
            </p>
          )}
        </div>
      )}
    </>
  );

  const descriptionBlock = () => (
    <>
      {(item?.descriptionFr || item?.descriptionEn) && (
        <p className="text-gray-500 text-sm overflow-hidden line-clamp-3">
          {lang === "fr" ? item?.descriptionFr : item?.descriptionEn}
        </p>
      )}
    </>
  );

  const configurations = () => {
    if (!item?.productConfigurations?.length) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {item.productConfigurations.map((option, index) => (
          <div
            key={index}
            className={`px-2 mt-2 ${
              index !== 0 ? "border-l border-gray-300" : ""
            }`}
          >
            <div className="flex flex-wrap items-center gap-4">
              {option.type === "color" &&
                option.values.map((val, i) => (
                  <div
                    key={i}
                    title={`${val.nameFr}${
                      val.price ? ` (+${val.price}$)` : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedColorIndex(`${index}-${i}`);
                    }}
                    className={`relative w-6 h-6 rounded-full border-2 cursor-pointer ${
                      selectedColorIndex === `${index}-${i}`
                        ? "border-muted ring-2 ring-muted"
                        : "border-gray-300"
                    }`}
                    style={{ backgroundColor: val.color }}
                  >
                    {val.price > 0 && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] p-1 rounded-full shadow leading-none">
                        +{val.price}$
                      </span>
                    )}
                  </div>
                ))}

              {["size", "material"].includes(option.type) &&
                option.values.map((val, i) => {
                  const selected = selectedOptionIndexes[option.id] === i;
                  return (
                    <div
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOptionIndexes((prev) => ({
                          ...prev,
                          [option.id]: i,
                        }));
                      }}
                      className={`relative px-3 py-1 rounded-full border text-sm cursor-pointer select-none flex items-center justify-center ${
                        selected
                          ? "border-muted ring-2 ring-muted bg-muted text-white"
                          : "border-gray-300 text-gray-700"
                      }`}
                      title={`${lang === "fr" ? val.nameFr : val.nameEn}${
                        val.price > 0 ? ` (+${val.price}$)` : ""
                      }`}
                    >
                      {lang === "fr" ? val.nameFr : val.nameEn}
                      {val.price > 0 && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] p-1 rounded-full shadow leading-none">
                          +{val.price.toFixed(2)}$
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const addressBlock = () => {
    if (!item?.showAddress || !item?.location?.formattedAddress) return null;
    return (
      <div>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            item.location.formattedAddress
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-secondary hover:underline mt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <MapPinIcon className="w-5 h-5 text-muted" />
          {item.location.formattedAddress}
        </a>
      </div>
    );
  };

  const tagsBlock = () => {
    if (!item?.tags?.length) return null;
    return (
      <div className="flex flex-wrap text-primary">
        {item.tags.map((tag, index) => (
          <span
            key={index}
            className="text-gray-500 bg-gray-200 text-sm px-2 py-1 rounded-full"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const ctaLabel = () => {
    if (inventoryLimits?.inventoryMode === "contact") return null;
    const idNum = parseInt(item?.priceTypeId, 10);
    if (isService && idNum !== 4) return t("global.book_now", "Réserver");
    return t("global.add_to_cart", "Ajouter au panier");
  };

  const handleAddToCartClick = async (e) => {
    e.stopPropagation();
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const url = isService
        ? `${base}/api/store/service-details/${item.id}`
        : `${base}/api/store/product-details/${item.id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("load error");
      const details = await res.json();

      if (isService) {
        const needsDates = parseInt(details?.priceTypeId, 10) !== 4;
        setModalServiceDetails(details);
        setModalProductDetails(null);
        setModalIsService(true);
        setModalAvailableDates(
          needsDates ? details?.availabilities ?? [] : null
        );
        setModalPricingMode(
          getPricingMode(details?.priceTypeId, priceTypesList, lang)
        );
      } else {
        setModalProductDetails(details);
        setModalServiceDetails(null);
        setModalIsService(false);
        setModalAvailableDates(null);
        setModalPricingMode("unit");
      }
    } catch {
      alert(t("global.load_error", "Impossible de charger les détails."));
    }
  };

  const ctaBlock = () => {
    if (inventoryLimits?.inventoryMode === "contact")
      return (
        <div className="mt-auto px-4 pb-4 w-full text-center">
          <p className="text-secondary font-medium text-sm">
            {t("global.contact_us_to_book")} :
            <span className="font-semibold text-primary">
              {formatPhoneNumber(item?.company?.phoneNumber) ??
                t("global.no_phone_available")}
            </span>
          </p>
        </div>
      );

    if (!showAddToCartButton) return null;

    return (
      <div className="mt-auto px-4 pb-4 w-full flex justify-center">
        <button
          onClick={handleAddToCartClick}
          className="w-full bg-primary text-white px-4 py-2 rounded-full hover:bg-primary/90 transition"
        >
          {ctaLabel()}
        </button>
      </div>
    );
  };

  const body = () => (
    <div className="flex flex-col p-4 gap-1">
      {titleCategory()}
      {descriptionBlock()}
      {configurations()}
      {addressBlock()}
      {tagsBlock()}
    </div>
  );

  return (
    <>
      <div
        key={item?.id}
        className="shadow-lg rounded-lg overflow-hidden h-full bg-white flex flex-col transition hover:shadow-xl min-h-[400px] max-h-[400px] hover:cursor-pointer hover:scale-105"
        onClick={openEditModal}
      >
        {headerImage()}
        {body()}
        {ctaBlock()}
      </div>

      {modalIsService && modalServiceDetails ? (
        <AddToCartServiceModal
          service={modalServiceDetails}
          availableDates={modalAvailableDates}
          pricingMode={modalPricingMode}
          onClose={(configured) => {
            if (configured) onAddToCart?.(configured);
            setModalServiceDetails(null);
            setModalIsService(false);
            setModalAvailableDates(null);
          }}
        />
      ) : modalProductDetails ? (
        <AddToCartModal
          product={modalProductDetails}
          onClose={(configured) => {
            if (configured) onAddToCart?.(configured);
            setModalProductDetails(null);
          }}
          eventDates={null}
        />
      ) : null}
    </>
  );
};

export default ProductCard;
